import React, { useContext, useRef } from 'react'
import { CSVLink } from 'react-csv'
import ReactGA from 'react-ga'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import moment from 'moment'

import PNGPreview from './PNGPreview'
import { GROUP_TYPE } from '../../constants/Filter'
import { ReactComponent as Download } from '../../img/download.svg'
import { Index } from '../../store/StateObjects'
import { Context } from '../RefinanceIndex'
import DownloadButton from '../StyledButton'
import StyledTooltip from '../StyledTooltip'
import { capitalize, convertToCanvas, getFilterData, getLabel } from '../util'

import '../../styles/download-content.css'

enum DOWNLOAD_TYPE {
  CSV = 'csv',
  PNG = 'png',
}

const useStyles = makeStyles({
  label: {
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'Libre Franklin',
    fontWeight: 300,
    lineHeight: '24px',
  },
})

const ColorRadio = withStyles({
  root: {
    color: '#C313A7',
    padding: '8px',
    '&$checked': {
      color: '#C313A7',
    },
  },
  checked: {},
})(Radio)

interface GroupedObject {
  [key: string]: Index[]
}

function groupByKey(array: Index[], key: keyof Omit<Index, 'week' | 'volume' | 'value'>) {
  return array.reduce((accu: GroupedObject, x: Index) => {
    accu[x[key]] = accu[x[key]] || []
    accu[x[key]].push(x)
    return accu
  }, {})
}

export const Content: React.FC = () => {
  const width = 752
  const height = 340
  const svgId = 'preview-svg'
  const svgRef = useRef<SVGSVGElement>(null)
  const [selectedValue, setSelectedValue] = React.useState<DOWNLOAD_TYPE>(DOWNLOAD_TYPE.CSV)
  const classes = useStyles()
  const { indexData, indexDetailType, groupBy } = useContext(Context)
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFormat = event.target.value as DOWNLOAD_TYPE
    setSelectedValue(selectedFormat)
  }

  const downloadRef = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(null)
  const titleName = capitalize(indexDetailType)

  const handleDownload = () => {
    if (selectedValue === DOWNLOAD_TYPE.CSV) {
      downloadRef?.current?.link.setAttribute('target', '_blank')
      downloadRef?.current?.link?.click()
    } else {
      convertToCanvas(svgId, `Refinance${titleName}Index_PEXAInsights`, width * 2, height * 2)
    }

    ReactGA.event({
      category: 'Refinance Index',
      action: 'Download',
      label: selectedValue,
    })
  }

  const groupedData = groupByKey(indexData, groupBy)

  const groupedDataArray = Object.keys(groupedData)
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
    .map((key) => {
      return groupedData[key]
    })
    .flat()

  const convertedArray = groupedDataArray.map((d) => {
    const { state, region, week, index_type: indexType, ...rest } = d
    return {
      week: moment(week).format('DD/MMM/YYYY'),
      state: getLabel(state, getFilterData(GROUP_TYPE.STATE)),
      region: getLabel(region, getFilterData(GROUP_TYPE.REGION)),
      index_type: getLabel(indexType, getFilterData(GROUP_TYPE.INDEX_TYPE)),
      ...rest,
    }
  })
  const titleType = titleName.toLowerCase()

  const headers = [
    { label: 'State', key: 'state' },
    { label: 'Region', key: 'region' },
    { label: 'Index Type', key: 'index_type' },
    { label: 'Week Ending Date', key: 'week' },
    { label: `Refinance ${titleName} Index`, key: `${titleType}.refi_index` },
    { label: '% Change Week', key: `${titleType}.change_week` },
    { label: '% Change Month', key: `${titleType}.change_month` },
    { label: '% Change Quarter', key: `${titleType}.change_quarter` },
    { label: '% Change Year', key: `${titleType}.change_year` },
  ]

  return (
    <div className="download-wrapper" data-testid="download-wrapper">
      <span className="download-title">Download</span>
      <span className="download-subtitle">Select download format:</span>
      <RadioGroup row aria-label="position" name="position" defaultValue="top">
        {Object.values(DOWNLOAD_TYPE).map((type) => {
          return (
            <FormControlLabel
              key={type}
              value="end"
              classes={classes}
              control={
                <ColorRadio
                  checked={selectedValue === type}
                  onChange={handleChange}
                  value={type}
                  name="download-type"
                  data-testid={`radio-button-${type}`}
                />
              }
              label={
                <>
                  <span>{`.${type.toUpperCase()}`}</span>
                  <StyledTooltip
                    label={type}
                    toolTipContent={
                      type === DOWNLOAD_TYPE.CSV
                        ? 'download selected data in CSV spreadsheet format'
                        : 'download a picture file of chart with current filter selections'
                    }
                  />
                </>
              }
            />
          )
        })}
      </RadioGroup>

      {selectedValue === DOWNLOAD_TYPE.PNG && (
        <>
          <span className="download-preview">Preview:</span>
          <PNGPreview svgId={svgId} svgRef={svgRef} width={width} height={height} />
        </>
      )}
      <CSVLink
        ref={downloadRef}
        data={convertedArray}
        headers={headers}
        data-testid="csv-download-link"
        aria-label="download csv link"
        filename={`Refinance${titleName}Index_PEXAInsights.csv`}
      />

      <DownloadButton startIcon={Download} width={160} handleClick={handleDownload} testId="download-button">
        Download
      </DownloadButton>
    </div>
  )
}

export default Content
